import React, { useState, useMemo, useEffect, useRef } from "react";
import tw from "tailwind-styled-components";
import {
  FaShoppingBag,
  FaMapMarkerAlt,
  FaExclamationCircle,
  FaCheckCircle,
  FaTruck,
  FaEye,
  FaArrowLeft,
  FaClock,
  FaTimes,
  FaUndo,
  FaBan,
  FaReceipt,
  FaBoxOpen,
  FaHeadset,
  FaImage,
  FaTrash,
  FaTimesCircle,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
// import useOrders from '../hooks/useOrders';
import useOrderDetails from "../hooks/useOrderDetails";
import moment from "moment";
import useOrderActions from "../hooks/useOrderActions";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import { toast } from "react-toastify";
import useReturns from "../hooks/useReturns";
import MuiSelect from "../components/Styles/MuiSelect";
import OrderSummary from "../components/Orders/OrderSummary";
import { OrderDetailsLoading } from "../components/SkeletonLoading/OrderDetailsLoading";

const MAX_FILE_SIZE_MB = 5;
const MAX_FILES_COUNT = 3;
const VALID_TYPES = ["image/jpeg", "image/png", "image/jpg"];

const OrderDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const user = useSelector((state) => state.UserReducer.user);
  const {
    data: selectedOrder,
    isLoading,
    error,
    refetch,
  } = useOrderDetails(orderId);
  // fetch only specific order details using orderId instead of all orders
  const { data: returnsData } = useReturns();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [selectedOrderAction, setSelectedOrderAction] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const { cancelOrder, isCancellingOrder, returnOrder, isReturningOrder } =
    useOrderActions();
  const [selectedReturnItemId, setSelectedReturnItemId] = useState(null); // specific product id to return
  const [returnDescription, setReturnDescription] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [returnImages, setReturnImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const imagesRef = useRef([]); // to always keep the latest images list (used for cleanup)

  useEffect(() => {
    imagesRef.current = returnImages;
  }, [returnImages]); // update ref whenever returnImages change to have latest images

  // cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (imagesRef.current) {
        imagesRef.current.forEach((file) => {
          if (file.preview) URL.revokeObjectURL(file.preview);
        });
      }
    };
  }, []);

  // File Selection Handler
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files); // convert FileList to Array
    setImageError("");

    if (returnImages.length + files.length > MAX_FILES_COUNT) {
      setImageError(`Maximum ${MAX_FILES_COUNT} images allowed.`);
      return;
    }

    const validFiles = []; // to store valid files
    let errorMsg = "";

    files.forEach((file) => {
      if (!VALID_TYPES.includes(file.type)) {
        errorMsg = "Only JPG and PNG formats are allowed.";
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        errorMsg = `File size must be under ${MAX_FILE_SIZE_MB}MB.`;
        return;
      }
      // create preview URL for image preview before upload
      Object.assign(file, { preview: URL.createObjectURL(file) });
      validFiles.push(file); // add valid files to the list
    });

    if (errorMsg) setImageError(errorMsg);
    setReturnImages((prev) => [...prev, ...validFiles]);
    e.target.value = ""; // Reset input to allow re-selecting same file
  };

  // Remove Image Handler
  const removeImage = (index) => {
    setReturnImages((prev) => {
      const newImages = [...prev];
      // Revoke preview URL immediately to free browser memory
      if (newImages[index].preview)
        URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1); // remove selected image
      return newImages;
    });
    setImageError(""); // clear errors after removal
  };

  const getOrderReturnData = (orderId) => {
    if (!returnsData || !Array.isArray(returnsData)) return null;
    return returnsData.find((r) => String(r.order_id) === String(orderId));
  };

  const formatCurrency = useMemo(
    () => (amount) => {
      return `₹${parseFloat(amount).toFixed(2)}`;
    },
    [],
  );

  const handleOpenCancelModal = (order) => {
    setSelectedOrderAction(order);
    setActionReason("");
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setSelectedOrderAction(null);
    setIsCancelModalOpen(false);
    setActionReason("");
  };

  const handleConfirmCancel = () => {
    if (!actionReason.trim()) {
      toast.error("Please provide a reason for cancellation.");
      return;
    }
    if (!selectedOrderAction) return;

    cancelOrder.mutate(
      { orderId: selectedOrderAction.id, reason: actionReason },
      {
        onSuccess: () => {
          handleCloseCancelModal();
          refetch();
        },
      },
    );
  };

  // Function to find return status for a specific item
  const getReturnStatus = (orderId, productId) => {
    if (!returnsData || !Array.isArray(returnsData)) return null;

    // Find the return object where both order_id and product_id match
    const foundReturn = returnsData.find(
      (r) =>
        String(r.order_id) === String(orderId) && // converting to string for comparison
        String(r.product_id) === String(productId),
    );

    return foundReturn ? foundReturn.status : null;
  };

  const handleOpenReturnModal = (order, item) => {
    setSelectedOrderAction(order);
    setSelectedReturnItemId(item.product_id);
    setActionReason("");
    setReturnDescription("");
    setIsReturnModalOpen(true);
  };

  const handleCloseReturnModal = () => {
    setSelectedOrderAction(null);
    setIsReturnModalOpen(false);
    setActionReason("");
    setReturnDescription("");
    returnImages.forEach((file) => {
      if (file.preview) URL.revokeObjectURL(file.preview);
    });
    setReturnImages([]);
    setImageError("");
    setFormErrors({});
  };

  const handleConfirmReturn = () => {
    const errors = {};

    if (!selectedReturnItemId) {
      errors.reason = "No item selected for return.";
    }

    if (!actionReason.trim()) {
      errors.reason = "Please provide a reason for return.";
    }

    if (!returnDescription.trim()) {
      errors.description = "Please provide a description for the return.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!selectedOrderAction) return;

    returnOrder.mutate(
      {
        orderId: selectedOrderAction.id,
        productId: selectedReturnItemId,
        reason: actionReason,
        description: returnDescription,
        images: returnImages,
      },
      {
        onSuccess: () => {
          handleCloseReturnModal();
          refetch();
        },
      },
    );
  };

  const getProductImage = (item) => {
    const imageUrl =
      item.product?.images?.[0]?.image_url ||
      item.product?.image_url ||
      item.image_url ||
      null;
    return imageUrl;
  };

  // Function to get default address from user profile data
  const getDefaultAddressFromProfile = () => {
    if (!user) return null;

    // Check if user has address data in their profile
    if (user.address_line_1 || user.city || user.state || user.postal_code) {
      return {
        address_line_1: user.address_line_1,
        address_line_2: user.address_line_2,
        city: user.city,
        state: user.state,
        postal_code: user.postal_code,
        country: user.country || "India",
      };
    }
    return null;
  };

  // Get pricing information for items
  const getItemPricing = (item) => {
    const price = item.unit_price || 0;

    return {
      displayPrice: parseFloat(price).toFixed(2),
    };
  };

  if (!user) {
    return (
      <PageContainer>
        <Header>
          <div className="flex items-center space-x-3">
            <BackButton onClick={() => navigate(-1)}>
              <FaArrowLeft className="w-5 h-5" />
            </BackButton>
            <div className="flex items-center space-x-2">
              <PageTitle>Order Details</PageTitle>
            </div>
          </div>
        </Header>
        <div className="flex-1 flex flex-col items-center justify-center pb-12">
          <FaExclamationCircle className="text-6xl text-gray-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-500 mb-2">
            Please Login or Register
          </h2>
          <p className="text-gray-500">
            You need to be logged in to view your orders.
          </p>
        </div>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Header>
          <div className="flex items-center space-x-3">
            <BackButton onClick={() => navigate(-1)}>
              <FaArrowLeft className="w-5 h-5" />
            </BackButton>
            <div className="flex items-center space-x-2">
              <PageTitle>Order Details</PageTitle>
            </div>
          </div>
        </Header>
        <OrderDetailsLoading />
      </PageContainer>
    );
  }

  if (!selectedOrder) {
    return (
      <PageContainer>
        <Header>
          <div className="flex items-center space-x-3">
            <BackButton onClick={() => navigate(-1)}>
              <FaArrowLeft className="w-5 h-5" />
            </BackButton>
            <div className="flex items-center space-x-2">
              <PageTitle>Order Details</PageTitle>
            </div>
          </div>
        </Header>
        <ErrorContainer>
          <ErrorTitle>Order Not Found</ErrorTitle>
          <ErrorMessage>The requested order could not be found.</ErrorMessage>
          <RetryButton onClick={() => navigate("/orders")}>
            Go to My Orders
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  const returnInfo = getOrderReturnData(selectedOrder.id);
  const returnStatus = returnInfo ? returnInfo.status : null;

  // Function to get order timeline
  const getOrderTimeline = (order) => {
    const steps = [];
    const addStep = (
      key,
      label,
      icon,
      isCompleted,
      isCurrent,
      isError = false,
      statusColor = null,
    ) => {
      steps.push({
        key,
        label,
        icon,
        isCompleted,
        isCurrent,
        isError,
        statusColor,
      });
    };

    const isPendingCompleted = true;
    const isPendingCurrent = order.status === "pending";
    addStep(
      "pending",
      "Pending",
      FaClock,
      isPendingCompleted,
      isPendingCurrent,
      false,
      isPendingCurrent ? "blue" : null,
    );

    if (order.status === "cancelled") {
      if (order.confirmed_at)
        addStep("confirmed", "Confirmed", FaCheckCircle, true, false);
      if (order.shipped_at) addStep("shipped", "Shipped", FaTruck, true, false);
      addStep("cancelled", "Cancelled", FaExclamationCircle, true, true, true);
      return steps;
    }

    const isConfirmed =
      ["confirmed", "shipped", "delivered"].includes(order.status) ||
      !!returnStatus;
    addStep(
      "confirmed",
      "Confirmed",
      FaCheckCircle,
      isConfirmed,
      order.status === "confirmed" && !returnStatus,
    );

    const isShipped =
      ["shipped", "delivered"].includes(order.status) || !!returnStatus;
    addStep(
      "shipped",
      "Shipped",
      FaTruck,
      isShipped,
      order.status === "shipped" && !returnStatus,
    );

    const isDelivered = order.status === "delivered" || !!returnStatus;
    addStep(
      "delivered",
      "Delivered",
      FaBoxOpen,
      isDelivered,
      order.status === "delivered" && !returnStatus,
    );

    if (returnStatus) {
      const isReturnRequested = true;
      const isReturnPending = returnStatus === "pending";

      addStep(
        "return_pending",
        "Return Pending",
        FaUndo,
        isReturnRequested,
        isReturnPending,
        false,
        isReturnPending ? "yellow" : null,
      );
      // Return Approved / Returned
      if (["approved", "returned", "refunded"].includes(returnStatus)) {
        const isReturnedCurrent =
          returnStatus === "approved" || returnStatus === "returned";
        addStep(
          "returned",
          "Return Approved",
          FaCheckCircle,
          true,
          isReturnedCurrent,
          false,
          isReturnedCurrent ? "green" : null,
        );
      }

      if (returnStatus === "refunded") {
        addStep("refunded", "Refunded", FaReceipt, true, true, false, "green");
      }

      if (returnStatus === "rejected") {
        addStep("rejected", "Return Rejected", FaBan, true, true, true);
      }
    }
    return steps;
  };

  const timelineSteps = getOrderTimeline(selectedOrder);
  const canCancel =
    selectedOrder.status === "pending" || selectedOrder.status === "confirmed";
  const canReturn = selectedOrder.status === "delivered";
  const shippingAddress =
    selectedOrder.shippingAddress || getDefaultAddressFromProfile();

  return (
    <PageContainer>
      <Header>
        <div className="flex items-center space-x-3">
          <BackButton onClick={() => navigate(-1)}>
            <FaArrowLeft className="w-5 h-5" />
          </BackButton>
          <div className="flex items-center space-x-2">
            {/* <FaShoppingBag className="w-5 h-5 text-kuality-blue" /> */}
            <PageTitle>Order Details</PageTitle>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          Order Id: {selectedOrder.id}
        </div>
      </Header>

      <ModalBody>
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-6">
          {/* order status timeline (in the middle on mobile view, top on desktop) */}
          <div className="w-full order-2 lg:order-1 lg:w-full">
            <StatusSection>
              <StepperContainer>
                {timelineSteps.map((step, index) => (
                  <Step key={step.key} $active={step.isCompleted}>
                    <StepIcon
                      $active={step.isCompleted}
                      $current={step.isCurrent}
                      $error={step.isError}
                      $statusColor={step.statusColor}
                    >
                      <step.icon />
                    </StepIcon>
                    <StepLabel $active={step.isCompleted || step.isCurrent}>
                      {step.label}
                    </StepLabel>
                    {index < timelineSteps.length - 1 && (
                      <StepLine
                        $active={
                          step.isCompleted &&
                          timelineSteps[index + 1].isCompleted
                        }
                      />
                    )}
                  </Step>
                ))}
              </StepperContainer>
              {canCancel && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-800">
                      Need to cancel your order?
                    </span>
                    <span className="text-xs text-gray-500">
                      You can cancel items before they are shipped.
                    </span>
                  </div>
                  <ActionLink
                    className="text-red-400 hover:text-red-600"
                    onClick={() => handleOpenCancelModal(selectedOrder)}
                  >
                    Cancel Order
                  </ActionLink>
                </div>
              )}
            </StatusSection>
          </div>

          {/* items ordered (on the top on mobile view, left side on desktop) */}
          <div className="w-full order-1 lg:order-2 lg:flex-1">
            <SectionTitle>Items Ordered</SectionTitle>
            <div className="space-y-3">
              {selectedOrder.items?.map((item, index) => {
                const imageUrl = getProductImage(item);
                const pricing = getItemPricing(item);
                const itemReturnStatus = getReturnStatus(
                  selectedOrder.id,
                  item.product_id,
                );
                const deliveryDate =
                  selectedOrder.delivered_at || selectedOrder.updated_at;
                const returnDeadline = deliveryDate
                  ? moment(deliveryDate).add(7, "days")
                  : moment();
                const isReturnWindowClosed = moment().isAfter(returnDeadline);

                return (
                  <OrderItemRow key={index}>
                    <div className="relative w-20 h-20 rounded-md overflow-hidden border border-gray-100 flex-shrink-0">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-400">
                          <FaShoppingBag />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 ml-4">
                      <h4 className="font-medium text-gray-900 line-clamp-2">
                        {item.product_name || `Product ${item.product_id}`}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(item.quantity * pricing.displayPrice)}
                        </p>
                      </div>
                      {selectedOrder.status === "delivered" &&
                        (itemReturnStatus ? (
                          // Case 1: Already Returned/Requested
                          <div
                            className={`text-xs font-bold px-3 py-1.5 rounded
                    ${
                      itemReturnStatus === "approved"
                        ? "bg-green-100 text-green-600"
                        : itemReturnStatus === "rejected"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                    }`}
                          >
                            {itemReturnStatus}
                          </div>
                        ) : isReturnWindowClosed ? (
                          // Case 2: Return Window Closed
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1.5 rounded cursor-not-allowed">
                              Return Expired
                            </span>
                          </div>
                        ) : (
                          // Case 3: Return Window Open
                          <div className="flex flex-col items-end gap-1">
                            <button
                              onClick={() =>
                                handleOpenReturnModal(selectedOrder, item)
                              }
                              className="text-xs font-medium text-white bg-kuality-blue hover:bg-kuality-dark px-3 py-1.5 rounded transition-colors"
                            >
                              Return
                            </button>
                            <span className="text-[10px] text-gray-500">
                              Valid until {returnDeadline.format("MMM DD")}
                            </span>
                          </div>
                        ))}
                    </div>
                  </OrderItemRow>
                );
              })}
            </div>
          </div>

          {/* order summary (on bottom on mobile view, right side on desktop) */}
          <div className="w-full order-3 lg:order-3 lg:w-1/3">
            <OrderSummary
              order={selectedOrder}
              shippingAddress={shippingAddress}
              formatCurrency={formatCurrency}
            />
          </div>
        </div>

        <SupportSection>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 rounded-full text-kuality-blue">
              <FaHeadset className="text-xl" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">
                Need help with this order?
              </h3>
              <ActionLink onClick={() => navigate("/help")}>
                Chat with us
              </ActionLink>
            </div>
          </div>
        </SupportSection>
      </ModalBody>

      <ConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirm={handleConfirmCancel}
        isLoading={isCancellingOrder}
        title="Cancel Order"
        message={`Are you sure you want to cancel your order? This action cannot be undone.`}
        confirmText="Yes, Cancel Order"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
      >
        {/* 'reason' text area passed as children */}
        <ReasonInputContainer>
          <ReasonLabel htmlFor="cancelReason">
            Reason for Cancellation (Required):
          </ReasonLabel>
          <ReasonTextArea
            id="cancelReason"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            rows="3"
            placeholder="Please provide a reason for cancelling the order..."
          />
        </ReasonInputContainer>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={isReturnModalOpen}
        onClose={handleCloseReturnModal}
        onConfirm={handleConfirmReturn}
        isLoading={isReturningOrder}
        title="Return Item"
        confirmText="Submit Return Request"
        confirmButtonColor="bg-kuality-blue hover:bg-kuality-blue-dark"
      >
        <div className="space-y-1">
          {/* display selected item details */}
          {selectedOrderAction?.items
            ?.filter((i) => i.product_id === selectedReturnItemId)
            .map((item) => (
              <div
                key={item.id}
                className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="w-12 h-12 rounded overflow-hidden border border-gray-200 flex-shrink-0 bg-white">
                  <img
                    src={getProductImage(item) || ""}
                    alt={item.product_name}
                    className="w-full h-full object-cover"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-bold text-gray-800 line-clamp-1">
                    {item.product_name}
                  </p>
                  <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                </div>
              </div>
            ))}

          <ReasonInputContainer>
            <ReasonLabel htmlFor="returnReason">
              Reason for Return<span className="text-red-500">*</span>
            </ReasonLabel>
            <MuiSelect
              id="returnReason"
              value={actionReason}
              onChange={(e) => {
                setActionReason(e.target.value);
                if (e.target.value) {
                  setFormErrors((prev) => ({ ...prev, reason: "" }));
                }
              }}
              placeholder="Select a reason"
              options={[
                { value: "Damaged Product", label: "Damaged Product" },
                { value: "Wrong Item Received", label: "Wrong Item Received" },
                { value: "Quality Issue", label: "Quality Issue" },
                { value: "Size/Fit Issue", label: "Size/Fit Issue" },
                { value: "Other", label: "Other" },
              ]}
            />
            {formErrors.reason && (
              <p className="text-red-500 text-xs">{formErrors.reason}</p>
            )}
          </ReasonInputContainer>

          <ReasonInputContainer>
            <ReasonLabel htmlFor="returnDesc">
              Description<span className="text-red-500">*</span>
            </ReasonLabel>
            <ReasonTextArea
              id="returnDesc"
              value={returnDescription}
              onChange={(e) => {
                setReturnDescription(e.target.value);
                if (e.target.value) {
                  setFormErrors((prev) => ({ ...prev, description: "" }));
                }
              }}
              rows="2"
              placeholder="Provide additional details about the return..."
            />
            {formErrors.description && (
              <p className="text-red-500 text-xs">{formErrors.description}</p>
            )}
          </ReasonInputContainer>
          <ReasonInputContainer>
            <div className="flex justify-between items-center mb-1">
              <ReasonLabel className="!mb-0">Upload Images</ReasonLabel>
              <span className="text-xs text-gray-400">
                Max {MAX_FILES_COUNT} • {MAX_FILE_SIZE_MB}MB Limit
              </span>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
                imageError
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 bg-gray-50 hover:bg-gray-100"
              }`}
            >
              <input
                type="file"
                id="return-images-input"
                multiple
                accept={VALID_TYPES.join(",")}
                onChange={handleImageChange}
                className="hidden"
                disabled={returnImages.length >= MAX_FILES_COUNT}
              />

              <label
                htmlFor="return-images-input"
                className={`flex flex-col items-center justify-center cursor-pointer ${
                  returnImages.length >= MAX_FILES_COUNT
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <FaImage className="text-2xl text-gray-400 mb-2" />
                <span className="text-sm font-medium text-gray-600">
                  {returnImages.length >= MAX_FILES_COUNT
                    ? "No more images can be added"
                    : "Click to upload images"}
                </span>
              </label>
            </div>

            {imageError && (
              <p className="flex items-center text-xs text-red-500 mt-2">
                <FaTimesCircle className="mr-1" /> {imageError}
              </p>
            )}

            {returnImages.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4">
                {returnImages.map((file, index) => (
                  <div
                    key={index}
                    className="relative w-16 h-16 border border-gray-200 rounded-lg overflow-hidden group shadow-sm"
                  >
                    <img
                      src={file.preview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      type="button"
                      className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    >
                      <FaTrash className="text-white text-sm hover:text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </ReasonInputContainer>
        </div>
      </ConfirmationModal>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = tw.div`
   min-h-screen w-full bg-white flex flex-col
`;

const Header = tw.div`
    flex items-center justify-between p-3 md:p-2 bg-white border-b border-gray-200 sticky top-0 z-50
`;

const PageTitle = tw.h1`
    text-lg font-semibold text-gray-900 tracking-wider md:text-xl
`;

const BackButton = tw.button`
     text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors
`;

const ModalBody = tw.div`
    p-5 md:px-16 md:py-8 overflow-y-auto flex-1 bg-gray-50
`;

const ErrorContainer = tw.div`
    flex-1 flex flex-col items-center justify-center text-center pb-12
`;

const ErrorTitle = tw.h3`
    text-lg font-semibold text-gray-800 mb-2
`;

const ErrorMessage = tw.p`
    text-gray-600 mb-4
`;

const RetryButton = tw.button`
    bg-kuality-blue text-white px-4 py-2 rounded-lg hover:bg-kuality-blue transition-colors
`;

// Stepper Components
const StatusSection = tw.div`
    bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-100
`;

const StepperContainer = tw.div`
    flex flex-col md:flex-row 
    items-start md:items-center 
    justify-between relative px-2 md:gap-0
`;

const Step = tw.div`
    flex flex-row md:flex-col 
    items-start md:items-center 
    relative z-10 flex-1 w-full md:w-auto 
    pb-8 md:pb-0 last:pb-0
`;

const StepIcon = tw.div`
    w-8 h-8 rounded-full flex items-center justify-center text-sm 
    transition-colors duration-300 relative z-20 shrink-0
    ${(p) => {
      if (p.$error) return "bg-red-500 text-white";
      if (p.$statusColor === "green") return "bg-green-600 text-white";
      if (p.$statusColor === "yellow") return "bg-yellow-600 text-white";
      if (p.$statusColor === "blue")
        return "bg-kuality-blue text-white shadow-lg shadow-blue-200 scale-110";
      if (p.$active) return "bg-green-600 text-white";
      return "bg-gray-200 text-gray-400";
    }}
`;

const StepLabel = tw.p`
    text-xs font-medium transition-colors duration-300 
    ml-4 mt-1.5 text-left md:ml-0 md:mt-2 md:text-center
    ${(p) => (p.$active ? "text-gray-900" : "text-gray-400")}
`;

const StepLine = tw.div`
    absolute -z-0 transition-colors duration-300 
    left-[15px] top-8 bottom-0 w-[2px] 
    md:top-4 md:left-[50%] md:w-full md:h-[2px] md:bottom-auto
    ${(p) => (p.$active ? "bg-green-500" : "bg-gray-200")}
`;

const SectionTitle = tw.h3`
    font-semibold text-gray-800 my-4 text-sm
`;

const OrderItemRow = tw.div`
    flex items-center p-4 bg-white rounded-lg border border-gray-100 shadow-sm hover:border-blue-100 transition-colors
`;

const SummaryCard = tw.div`
    bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-0 my-4
`;

const SupportSection = tw.div`
    my-4 bg-white border border-gray-200 rounded-xl p-6 shadow-sm
`;

const ActionLink = tw.button`
    text-sm font-medium text-kuality-blue hover:text-blue-800 hover:underline decoration-1 underline-offset-2
`;

const ReasonInputContainer = tw.div`
    w-full
`;

const ReasonLabel = tw.label`
    block text-sm font-medium text-gray-700 mb-1
`;

const ReasonTextArea = tw.textarea`
    w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#053264] text-sm bg-white
    placeholder-gray-400 disabled:opacity-50
`;

export default OrderDetails;
